import React, { useState, useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import { useTheme } from '../contexts/ThemeContext';
import { 
  BookOpen, ChevronRight, ChevronDown, Search, Layers, Heart, UserCircle, 
  FileSignature, DollarSign, CheckSquare, Home, BarChart3, Target, Calendar,
  Film, MessageSquarePlus, Users, Package, CreditCard, Database, Video,
  ListTodo, ClipboardList, GitBranch, Mail, FileText, Settings, Shield,
  ExternalLink, Play, AlertTriangle, CheckCircle, Info, Lightbulb, Crosshair
} from 'lucide-react';
import { sanitizeHTML } from '../lib/sanitize';

const Documentation = () => {
  const { isDark } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    'getting-started': true,
    'crm': false,
    'lead-finder': false,
    'tasks': false,
    'analytics': false,
    'operations': false,
    'admin': false,
    'settings': false,
    'troubleshooting': false
  });
  const [activeArticle, setActiveArticle] = useState('overview');

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Documentation structure
  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: BookOpen,
      articles: [
        { id: 'overview', title: 'System Overview' },
        { id: 'first-steps', title: 'First Steps After Login' },
        { id: 'navigation', title: 'Navigating the Interface' },
        { id: 'help-videos', title: 'Using Help Videos' }
      ]
    },
    {
      id: 'crm',
      title: 'CRM Features',
      icon: Heart,
      articles: [
        { id: 'member-tracker', title: 'Member Tracker Board' },
        { id: 'companies', title: 'Companies Management' },
        { id: 'contacts', title: 'Contacts Management' },
        { id: 'proposals', title: 'Creating Proposals' },
        { id: 'contracts', title: 'Contracts & Payments' },
        { id: 'card-details', title: 'Card Details & Notes' },
        { id: 'relationship-status', title: 'Relationship Statuses' }
      ]
    },
    {
      id: 'lead-finder',
      title: 'Lead Finder',
      icon: Crosshair,
      articles: [
        { id: 'lf-overview', title: 'What is Lead Finder?' },
        { id: 'lf-getting-started', title: 'Getting Started' },
        { id: 'lf-dashboard', title: 'Dashboard' },
        { id: 'lf-icp', title: 'Ideal Customer Profiles (ICPs)' },
        { id: 'lf-tiers', title: 'Membership Tiers' },
        { id: 'lf-pipeline', title: 'Lead Pipeline' },
        { id: 'lf-import', title: 'Importing Leads' },
        { id: 'lf-scoring', title: 'AI Lead Scoring' },
        { id: 'lf-sequences', title: 'Outreach Sequences' },
        { id: 'lf-templates', title: 'Message Templates' },
        { id: 'lf-extension', title: 'Chrome Extension' },
        { id: 'lf-analytics', title: 'Analytics' },
        { id: 'lf-admin', title: 'Admin Configuration' },
        { id: 'lf-safety', title: 'Safety & Rate Limits' },
        { id: 'lf-token', title: 'Token Optimization' }
      ]
    },
    {
      id: 'tasks',
      title: 'Task Management',
      icon: CheckSquare,
      articles: [
        { id: 'tasks-overview', title: 'Tasks Overview' },
        { id: 'creating-tasks', title: 'Creating Tasks' },
        { id: 'task-views', title: 'Task Views & Filters' },
        { id: 'task-urgency', title: 'Task Urgency System' }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics & Reporting',
      icon: BarChart3,
      articles: [
        { id: 'dashboard', title: 'Dashboard Overview' },
        { id: 'intelligence', title: 'Intelligence Dashboard' },
        { id: 'goals', title: 'Goals Tracking' }
      ]
    },
    {
      id: 'operations',
      title: 'Operations',
      icon: Film,
      articles: [
        { id: 'events-calendar', title: 'Events Calendar' },
        { id: 'media-production', title: 'Media Production Board' },
        { id: 'operations-checklist', title: 'Operations Checklist' }
      ]
    },
    {
      id: 'admin',
      title: 'Administration',
      icon: Shield,
      articles: [
        { id: 'admin-overview', title: 'Admin Overview' },
        { id: 'user-management', title: 'User Management' },
        { id: 'products', title: 'Products Catalog' },
        { id: 'payment-settings', title: 'Payment Settings' },
        { id: 'data-import', title: 'Data Import/Export' },
        { id: 'workflow-editor', title: 'Workflow Editor' },
        { id: 'email-templates', title: 'Email Templates' },
        { id: 'system-emails', title: 'System Emails' },
        { id: 'integrations', title: 'Integrations (Brevo, Gmail)' },
        { id: 'help-videos-admin', title: 'Managing Help Videos' }
      ]
    },
    {
      id: 'settings',
      title: 'User Settings',
      icon: Settings,
      articles: [
        { id: 'profile', title: 'Your Profile' },
        { id: 'theme', title: 'Theme Settings' }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: AlertTriangle,
      articles: [
        { id: 'common-issues', title: 'Common Issues' },
        { id: 'support-requests', title: 'Submitting Support Requests' }
      ]
    }
  ];

  // Article content
  const articleContent = {
    // GETTING STARTED
    'overview': {
      title: 'System Overview',
      content: `
## Welcome to QSECDEF CRM

QSECDEF CRM is a comprehensive customer relationship management system designed for managing B2B memberships, events, proposals, and media production workflows.

### Key Features

| Area | Features |
|------|----------|
| **CRM** | Member tracking, company management, contacts, proposals |
| **Payments** | Contract management, Stripe/Revolut integration, payment recording |
| **Tasks** | Task management with urgency tracking and multiple views |
| **Analytics** | Dashboard metrics, intelligence reports, goal tracking |
| **Operations** | Events calendar, media production workflows |

### User Roles

The system supports two user roles:

- **Admin** - Full access to all features including user management, product catalog, payment settings, and system configuration
- **User** - Access to CRM features, tasks, and analytics with read-only access to certain admin areas

### Quick Navigation

Use the sidebar on the left to navigate between sections. The sidebar is organized into:

1. **CRM** - Member Tracker, Companies, Contacts, Proposals, Contracts
2. **Tasks** - Task management with urgency indicators
3. **Analytics** - Dashboard, Intelligence, Goals
4. **Operations** - Events Calendar, Media Production
5. **Support** - Documentation, Bug Requests
6. **Admin** - System configuration (Admin only)
      `
    },
    'first-steps': {
      title: 'First Steps After Login',
      content: `
## Getting Started

After logging in with your Google account, here's what to do first:

### 1. Review the Dashboard

Navigate to **Analytics → Dashboard** to see:
- Key performance metrics
- Recent activity
- Task urgency indicators

### 2. Explore the Member Tracker

Go to **CRM → Member Tracker** to see your Kanban board with all members organized by status columns.

### 3. Check Your Tasks

Click on **Tasks** in the sidebar to see:
- Assigned tasks
- Today's due items
- Any overdue tasks (shown with red badges)

### 4. Set Up Your Profile

Go to **Settings** (gear icon) to:
- Add your job title
- Review your account details

### Tips for New Users

> **Tip:** Look for the green play button in the header - it opens tutorial videos for each section!

> **Tip:** Use the theme toggle (sun/moon icon) to switch between dark and light modes.

> **Tip:** The sidebar can be collapsed using the arrow button to maximize your workspace.
      `
    },
    'navigation': {
      title: 'Navigating the Interface',
      content: `
## Interface Navigation

### Sidebar Navigation

The left sidebar contains all main navigation options:

| Section | Purpose |
|---------|---------|
| **CRM** | Customer relationship management features |
| **Tasks** | Personal and team task management |
| **Analytics** | Reports and dashboards |
| **Operations** | Events and media workflows |
| **Support** | Documentation and bug reporting |
| **Admin** | System settings (admin only) |

### Header Bar

The top header contains:
- **Page Title** - Shows current page name
- **Help Video** - Green play button for tutorial videos
- **Theme Toggle** - Switch between dark/light modes
- **User Menu** - Access settings and logout

### Collapsing the Sidebar

Click the **←** arrow in the sidebar header to collapse it. This gives you more screen space for your work. Click **→** to expand it again.

### Breadcrumb Navigation

Some pages show breadcrumbs at the top to help you navigate back to parent sections.

### Keyboard Shortcuts

Currently, navigation is primarily mouse/touch-based. Use browser back/forward buttons to navigate between recently visited pages.
      `
    },
    'help-videos': {
      title: 'Using Help Videos',
      content: `
## Tutorial Videos

Each section of the CRM has an optional tutorial video to help you understand its features.

### Accessing Videos

1. Navigate to any page in the system
2. Look for the **green play button** (▶) in the header
3. Click it to open a video overlay
4. Video plays automatically
5. Click **X** to close the overlay

### Available Video Topics

Videos are available for:
- Dashboard
- Member Tracker
- Companies
- Contacts
- Proposals
- Contracts
- Tasks
- Events Calendar
- Media Production
- And more...

### Video Not Available?

If you see "No tutorial video available," this means the administrator hasn't yet configured a video for that section.

> **Admin Note:** Videos can be configured at **Admin → Help Videos**
      `
    },

    // LEAD FINDER
    'lf-overview': {
      title: 'What is Lead Finder?',
      content: `
## What is Lead Finder?

Lead Finder is an automated LinkedIn lead generation system built into your CRM. It discovers potential leads, qualifies them against your criteria using AI, and manages outreach campaigns — all while mimicking natural human behavior to keep your LinkedIn account safe.

### How It Works (The Simple Version)

1. **Define your ideal customer** — Create an Ideal Customer Profile (ICP) with job titles, industries, locations, and keywords
2. **Find leads** — Import from Excel/CSV or scrape LinkedIn followers from company pages
3. **AI scores each lead** — Your configured AI model rates each lead 0-100 against your ICP
4. **Outreach** — Qualified leads enter automated sequences (LinkedIn messages, emails) with personalized templates
5. **Convert** — When a lead is ready, the system auto-creates a CRM card and follow-up task

### Key Features

| Feature | Description |
|---------|-------------|
| **ICP Management** | Create multiple ICPs with weighted criteria and auto-qualify thresholds |
| **Multi-Tier Qualification** | Professional / Enterprise / Expert tiers with different actions per tier |
| **Lead Pipeline** | Visual pipeline tracking leads from import through conversion |
| **Bulk Import** | Upload 10,000+ leads from Excel/CSV with automatic deduplication |
| **AI Scoring** | Pluggable LLM providers (ChatGPT, Claude, DeepSeek, Ollama, custom) |
| **Outreach Sequences** | Multi-step campaigns with LinkedIn + email, merge tags, conditions |
| **Message Templates** | Reusable templates with 13 merge tags for personalization |
| **Safety System** | 3-layer rate limiting, behavioral mimicry, ramp-up protection |
| **Analytics** | Conversion funnels, ICP performance, sequence metrics, score distribution |
| **Chrome Extension** | Session management, behavioral learning, action execution |

### Navigation

Use the top navigation bar on any Lead Finder page to quickly switch between sections: Dashboard, Pipeline, ICPs, Sequences, Templates, Import, Analytics, Extension, and Admin.
`
    },

    'lf-getting-started': {
      title: 'Getting Started with Lead Finder',
      content: `
## Getting Started

### Step 1: Configure Admin Settings

Go to **Lead Finder > Admin** and set up:

1. **Global Settings** — Company-wide daily limits for automated actions
2. **LLM Config** — Connect your AI provider (OpenAI, Claude, etc.) for lead scoring
3. **Safety** — Enable safety features (recommended: all enabled by default)

### Step 2: Create Your First ICP

Go to **Lead Finder > ICPs** and click "New ICP":

1. Give it a name (e.g., "Enterprise CTO - Cybersecurity")
2. Add job titles you're targeting (CTO, VP Engineering, CISO)
3. Add target industries and locations
4. Set weights (how important each criterion is, out of 100 total)
5. Set the auto-qualify threshold (e.g., 70 = leads scoring 70+ are automatically qualified)
6. Assign a membership tier if using multi-tier qualification

### Step 3: Import Your Leads

Go to **Lead Finder > Import**:

1. Download the template (CSV or Excel) to see the required format
2. Upload your file with lead data
3. Map your columns to lead fields
4. Review the confirmation summary
5. Click "Confirm & Import"

### Step 4: Install the Chrome Extension (Optional)

Go to **Lead Finder > Extension** for download and installation instructions. The extension enables LinkedIn scraping and behavioral learning.

### Step 5: Review Your Pipeline

Go to **Lead Finder > Pipeline** to see all your leads. From here you can:
- Filter by stage, search by name/company
- Change lead stages
- Select leads and bulk-enroll them in outreach sequences
`
    },

    'lf-dashboard': {
      title: 'Lead Finder Dashboard',
      content: `
## Dashboard

The Lead Finder Dashboard (/lead-finder) is your central overview showing:

### Stat Cards
- **Total Leads** — All leads in the system
- **Qualified** — Leads that passed ICP scoring
- **Contacted** — Leads that have been reached out to
- **Converted** — Leads that became CRM cards

### Pipeline Overview
A horizontal bar chart showing the count of leads in each stage: Scraped, Imported, Scoring, Qualified, Disqualified, Contacted, Replied, Converted, Lost.

### Quick Actions
One-click links to all Lead Finder sections: ICPs, Import, Scraping Jobs, Sequences, Templates, Analytics, Admin.

### Conversion Metrics
- **Qualification Rate** — % of total leads that qualified
- **Conversion Rate** — % of total leads that converted

### Recent Leads
A table showing the 10 most recently added leads with name, company, stage, and ICP score.
`
    },

    'lf-icp': {
      title: 'Ideal Customer Profiles (ICPs)',
      content: `
## Ideal Customer Profiles

An ICP defines what makes a lead a good fit for your business. Create multiple ICPs for different target segments.

### Criteria (with Weights)

Each criterion has a **weight** (importance) that must total 100 across all criteria:

| Criterion | What It Matches | Example Values |
|-----------|----------------|----------------|
| **Job Titles** | Lead's current job title | CTO, VP Engineering, CISO |
| **Industries** | Lead's company industry | Cybersecurity, Defense, Technology |
| **Company Size** | Number of employees | 50 - 5000 |
| **Locations** | Company location (with synonym matching) | United Kingdom, USA, Germany |
| **Keywords** | Text in headline, summary, experience | quantum, zero trust, SOC |
| **Seniority Level** | Career level indicators | Director, VP, C-Suite |

### Location Matching

The system handles location synonyms automatically:
- "USA" / "United States" / "America" / "US" all match each other
- "UK" / "United Kingdom" / "England" / "Great Britain" all match
- City names map to countries: "London" matches "United Kingdom", "New York" matches "United States"

### Thresholds

- **Auto-qualify threshold** (e.g., 70) — Leads scoring at or above this are automatically moved to "Qualified"
- **Auto-disqualify threshold** (e.g., 30) — Leads scoring below this are automatically moved to "Disqualified"

### Exclusions

Automatically reject leads matching:
- Excluded job titles (e.g., "Intern", "Student")
- Excluded companies (e.g., competitors)
- Excluded email domains

### Adding Tags

Type a value and press **Enter** to add it as a tag. You can add multiple values to any criterion. Tags are case-insensitive when matching.
`
    },

    'lf-tiers': {
      title: 'Membership Tiers',
      content: `
## Multi-Tier Qualification

Leads can be qualified into different membership tiers, each with different actions:

### Tier Setup

| Tier | Priority | Action on Qualification |
|------|----------|------------------------|
| **Professional** ($149) | 1 (scored first) | Sends discount code via message/email |
| **Enterprise** | 2 | Creates CRM card + follow-up task |
| **Expert** | 3 (scored last) | Creates CRM card + follow-up task |

### How Bottom-Up Scoring Works

1. The system scores the lead against the **Professional** ICP first (lowest bar)
2. If qualified, marks them as Professional
3. Then scores against **Enterprise** — if qualified, overrides to Enterprise
4. Then scores against **Expert** — if qualified, overrides to Expert
5. The lead gets the **highest tier** they qualify for

### Configuring Tiers

When creating/editing an ICP, set:
- **Tier** — Professional, Enterprise, or Expert
- **Priority** — 1 = scored first (lowest bar), 3 = scored last (highest bar)
- **Action** — "Create CRM Card" or "Send Discount Code"
`
    },

    'lf-pipeline': {
      title: 'Lead Pipeline',
      content: `
## Lead Pipeline

The pipeline (/lead-finder/pipeline) shows all leads in a filterable table.

### Lead Stages

| Stage | Description |
|-------|-------------|
| **Imported** | Uploaded from Excel/CSV |
| **Scraped** | Discovered via LinkedIn follower scraping |
| **Scoring** | Currently being scored by AI |
| **Qualified** | Passed ICP scoring threshold |
| **Disqualified** | Below ICP scoring threshold |
| **Contacted** | Outreach message sent |
| **Replied** | Lead responded to outreach |
| **Converted** | CRM card created (auto or manual) |
| **Lost** | Lead rejected or unresponsive |
| **Opted Out** | Lead requested no further contact |

### Features

- **Search** — Find leads by name, company, email, or headline
- **Stage Filter** — Show only leads in a specific stage
- **Inline Stage Change** — Change a lead's stage directly from the dropdown in the table
- **Add Lead** — Manually add a lead with name, email, company, title, LinkedIn URL
- **Quick Enroll** — Select multiple leads and enroll them in an outreach sequence in one click
- **Bulk Delete** — Select and delete multiple leads at once

### Quick Enroll

1. Select leads using the checkboxes
2. Click the purple "Enroll (N)" button
3. Choose a sequence from the dropdown
4. Click "Enroll" — leads already enrolled or opted-out are automatically skipped

### Conversion

When you change a lead's stage to "Converted":
- A **CRM card** is automatically created (or contact added to existing company card)
- A **follow-up task** is created and assigned to you
- If card creation fails, the lead reverts to "Replied" stage with an error notification
`
    },

    'lf-import': {
      title: 'Importing Leads',
      content: `
## Importing Leads

Import leads from Excel (.xlsx) or CSV files at /lead-finder/import.

### Step 1: Download Template

Click "CSV Template" or "Excel Template" to download a file with the correct column headers:

| Column | Description | Required? |
|--------|-------------|-----------|
| First Name | Lead's first name | Yes (or Email) |
| Last Name | Lead's last name | No |
| Email | Email address | Yes (or First Name) |
| Company | Company name | No |
| Job Title | Current job title | No |
| LinkedIn URL | Profile URL | No |
| Location | City, Country | No |
| Industry | Company industry | No |
| Phone | Phone number | No |
| Headline | LinkedIn headline or bio | No |

### Step 2: Upload

Click "Choose File" and select your Excel or CSV file (max 50MB).

### Step 3: Map Columns

The system auto-suggests column mappings. Verify each mapping or change via the dropdown. Columns you don't want to import can be set to "-- Skip --".

### Step 4: Confirm

Review the summary showing total rows, columns mapped, and columns skipped. Duplicates (by email or LinkedIn URL) are automatically skipped.

### Step 5: Results

After import, you'll see how many leads were imported, how many duplicates were skipped, and how many had errors.

### Import History

Scroll down to see all previous imports with their results.
`
    },

    'lf-scoring': {
      title: 'AI Lead Scoring',
      content: `
## AI Lead Scoring

Leads are scored against your ICP criteria using AI. The system uses a multi-layer approach to minimize cost.

### Scoring Layers

1. **Pre-Filter (Rules-Based)** — Checks job title, location, industry against ICP using keyword matching. Leads scoring below 15 are auto-disqualified without calling the AI. Saves ~60-70% of API costs.

2. **Company Cache** — If leads from the same company have already been scored, the company-level scores (industry, size, location) are reused. Cache expires after 30 days.

3. **Batch Scoring** — Multiple leads are scored in a single AI call. 50 leads per batch for imports, 5 leads per batch for scraping. If a batch fails, the system falls back to scoring each lead individually.

### Score Breakdown

Each lead receives:
- **Score** (0-100) — Overall match score
- **Confidence** (High/Medium/Low)
- **Reasoning** — One paragraph explaining the score
- **Talking Points** — 3 suggested conversation starters

### Configuring AI Provider

Go to **Admin > LLM Config**:
1. Select your preferred provider (OpenAI, Claude, DeepSeek, Ollama, Custom)
2. Enter the API key
3. Select the model
4. The endpoint URL is pre-filled for standard providers
5. Click "Save LLM Config"
6. Test with "Test Connection"

### Scoring a Lead

- **Individual**: Open a lead's detail page and click "Score"
- **Batch**: Select leads in the pipeline and use batch scoring
- **Auto**: Imported leads can be auto-scored if you select an ICP during import
`
    },

    'lf-sequences': {
      title: 'Outreach Sequences',
      content: `
## Outreach Sequences

Sequences are multi-step automated outreach campaigns at /lead-finder/sequences.

### Step Types

| Type | Description |
|------|-------------|
| **LinkedIn Connection** | Sends a connection request with a message |
| **LinkedIn Message** | Sends a direct message (requires existing connection) |
| **Email** | Sends an email (requires email address on lead) |
| **Wait** | Pauses for a set number of days with optional conditions |

### Creating a Sequence

1. Click "New Sequence"
2. Enter a name and description
3. Add steps — each step has a type, delay (days), and message template
4. Use merge tags to personalize: {first_name}, {company}, {title}, etc.
5. For "Wait" steps, set conditions: proceed always, only if connection accepted, only if no reply
6. Save the sequence

### Example Sequence

| Step | Type | Delay | Message |
|------|------|-------|---------|
| 1 | LinkedIn Connection | 0 days | "Hi {first_name}, great work at {company}!" |
| 2 | Wait | 3 days | Condition: connection_accepted |
| 3 | LinkedIn Message | 0 days | "Thanks for connecting! I'd love to discuss..." |
| 4 | Wait | 4 days | Condition: no_reply |
| 5 | Email | 0 days | "Hi {first_name}, I reached out on LinkedIn about..." |

### Enrolling Leads

- From the Sequences page: Click a sequence and use "Enroll" to add leads
- From the Pipeline: Select leads, click "Enroll (N)", choose a sequence
- Leads that have opted out or are already enrolled are automatically skipped

### Pausing / Resuming

Use the Pause/Resume buttons on each sequence to control all enrolled leads at once.
`
    },

    'lf-templates': {
      title: 'Message Templates',
      content: `
## Message Templates

Reusable message templates at /lead-finder/templates.

### Creating a Template

1. Click "New Template"
2. Choose channel: LinkedIn or Email
3. Choose type: Connection Request, Direct Message, or Email
4. Write your message using merge tags for personalization
5. For email templates, add a subject line

### Available Merge Tags

| Tag | Replaces With |
|-----|--------------|
| {first_name} | Lead's first name |
| {last_name} | Lead's last name |
| {full_name} | Full name |
| {company} | Current company |
| {title} | Job title |
| {headline} | LinkedIn headline |
| {location} | Location |
| {industry} | Industry |
| {email} | Email address |
| {score} | ICP score |
| {talking_point_1} | First AI-generated talking point |
| {talking_point_2} | Second talking point |
| {talking_point_3} | Third talking point |

### Preview

Use the "Preview" button to see how a template will look with actual lead data before sending.
`
    },

    'lf-extension': {
      title: 'Chrome Extension',
      content: `
## Chrome Extension

The self-hosted Chrome extension handles LinkedIn session management and behavioral learning.

### Installation

1. Go to **Lead Finder > Extension**
2. Click "Download Extension (ZIP)"
3. Unzip to a folder on your computer
4. Open chrome://extensions/ in Chrome
5. Enable "Developer mode" (top-right toggle)
6. Click "Load unpacked" and select the unzipped folder

### Configuration

1. Click the extension icon in Chrome toolbar
2. Enter your Backend URL (your CRM domain)
3. Enter your CRM session token
4. Click "Save & Connect"

### Training Mode

Training Mode learns your natural LinkedIn browsing patterns over 7-14 days:
- When you typically browse LinkedIn
- How fast you navigate between pages
- How long you spend viewing profiles
- Your daily activity volume

This data is used to make automation look natural. Toggle Training Mode on in the extension popup.

### Connection Test

On the Extension page, click "Test Connection" to verify:
- **Green** = Extension connected, LinkedIn session active
- **Red** = Not connected or session expired

### Security

- Session cookies are encrypted before transmission
- All communication uses HTTPS
- Extension auto-disconnects when CRM session expires
`
    },

    'lf-analytics': {
      title: 'Lead Finder Analytics',
      content: `
## Analytics

The Analytics page (/lead-finder/analytics) provides performance insights.

### Period Selector

Choose 7, 30, or 90 days to adjust all metrics.

### Stat Cards

- **Total Leads** — All leads with new leads in the selected period
- **Conversion Rate** — % of all leads that reached "Converted" stage
- **Reply Rate** — % of outreach messages that received replies
- **Active Sequences** — Number of running sequences with total enrolled leads

### Conversion Funnel

A horizontal bar chart showing how leads flow through each stage, with counts and percentages. Stages: Imported → Scraped → Scoring → Qualified → Contacted → Replied → Converted.

### Stage Conversion Rates

Qualification, Contact, Reply, and Conversion rates showing stage-to-stage efficiency.

### ICP Score Distribution

A histogram showing how many leads fall in each score bracket (0-10, 11-20, ..., 91-100). Helps you understand if your ICP criteria are too strict or too loose.

### ICP Performance Table

Compare ICPs by leads scored, qualification rate, conversion rate, and average score.

### Sequence Performance Table

Compare sequences by enrollment, replies, conversions, and reply/conversion rates.

### Activity Timeline

Daily chart showing leads created and messages sent over the selected period.
`
    },

    'lf-admin': {
      title: 'Admin Configuration',
      content: `
## Admin Configuration

Access at /lead-finder/admin. Five tabs for system-wide settings.

### Global Settings Tab

**Company-Wide Daily Limits** — Maximum automated actions across ALL users per day:
- Total Actions (recommended: 300-500)
- Scraping Requests, Connection Requests, Messages, Emails

**Per-User Limits** — Default daily limits for each user:
- Profile Views (safe: 50-80/day)
- Connection Requests (safe: 20-25/day)
- Messages, Emails, Scraping Pages

**Follower Scraping Limits:**
- **Max Per Job** — Max followers from one company/profile in a single job
- **Max Per Day** — Max followers across all jobs in 24 hours
- **Cooldown** — Wait time before re-scraping the same source (prevents detection)

**Ramp-Up** — Gradual increase over 4 weeks (Week 1: 25%, Week 2: 50%, Week 3: 75%, Week 4: 100%)

### User Limits Tab

Override default limits for individual users. Leave blank to use global defaults.

### LLM Config Tab

Configure your AI provider for lead scoring:
- **Active Provider** — Which AI is used for scoring
- **API Key** — Your provider's API key
- **Model** — Which model to use (e.g., gpt-4o, claude-sonnet-4)
- **Endpoint** — The API URL (pre-filled for standard providers)
- **Min Score Threshold** — Auto-qualify leads scoring above this

### Safety Tab

- **Require active CRM session** — Automation only runs when user is logged in
- **Pause on LinkedIn** — Pauses when user is actively browsing LinkedIn
- **Emergency stop** — Instant halt of all automation
- **Error thresholds** — Auto-pause after consecutive failures

### Scraping Jobs Tab

View and manage all LinkedIn follower scraping jobs. Cancel pending jobs or refresh status.

### Hover over any (i) icon for detailed explanation of that setting.
`
    },

    'lf-safety': {
      title: 'Safety & Rate Limits',
      content: `
## Safety & Rate Limits

The system uses three layers of protection to keep your LinkedIn account safe.

### Layer 1: Per-User Limits

Each user has daily maximums (configurable in Admin):
- Profile views: 50/day
- Connection requests: 20/day
- Messages: 25/day
- Emails: 30/day

When a limit is hit, that action type stops for the day. **Hard stop — no exceptions.**

### Layer 2: Company-Wide Limits

A global cap across all users combined (500 total actions/day by default). Prevents the organization from over-automating even if individual limits are fine.

### Layer 3: Ramp-Up

New users start at reduced limits and gradually increase:
- Week 1: 25% of full limits
- Week 2: 50%
- Week 3: 75%
- Week 4+: 100%

### Behavioral Mimicry

The Chrome extension learns your natural LinkedIn patterns and makes automation look human:
- Actions only execute during your normal active hours
- Random delays between actions (matching your average timing)
- Session breaks after 15-20 minutes (matching your typical session length)

### Scraping Cooldowns

- Company pages: 24-hour cooldown before re-scraping the same page
- Personal profiles: 48-hour cooldown
- Cooldowns are global — applies across all users

### Emergency Stop

Admin can instantly halt all automation with one click in Admin > Safety. All pending and processing jobs are cancelled immediately.
`
    },

    'lf-token': {
      title: 'Token Optimization',
      content: `
## Token Optimization

The system minimizes AI API costs through intelligent filtering and caching.

### Pre-Filter (Layer 1)

Before calling the AI, a rule-based check scores each lead:
- Job title keyword match
- Industry match
- Location match (with synonym normalization)
- Keyword presence in headline/summary
- Seniority indicators

Leads scoring below 15/100 on rules alone are **auto-disqualified without any AI call**. This saves 60-70% of token costs.

### Company Cache (Layer 2)

Company-level data (industry, size, location scores) is cached for 30 days. When multiple leads from the same company are scored, the company data is reused — only individual-level data (title, seniority) goes to the AI.

### Batch Scoring (Layer 3)

Instead of one AI call per lead:
- **Imports:** 50 leads per batch call
- **Scraping:** 5 leads per batch call

If a batch returns invalid JSON, the system automatically falls back to scoring those leads individually.

### Cost Estimate

| Scenario | Without Optimization | With Optimization |
|----------|---------------------|------------------|
| 10,000 imported leads | ~5M tokens ($15-25) | ~1.5M tokens ($5-8) |
| 500 scraped leads/day | ~250K tokens/day | ~75K tokens/day |

### Monitoring

Track token usage in Admin > LLM Config. Set monthly budgets per provider to prevent unexpected costs.
`
    },

    // CRM FEATURES
    'member-tracker': {
      title: 'Member Tracker Board',
      content: `
## Member Tracker Board

The Member Tracker is a **Kanban-style board** for managing your entire sales pipeline and member lifecycle.

### Board Columns

Cards move through customizable columns representing different stages:

| Default Columns | Purpose |
|-----------------|---------|
| **New Leads** | Fresh leads requiring qualification |
| **Qualified** | Leads that meet your criteria |
| **Proposal Sent** | Proposal has been delivered |
| **Negotiation** | Active discussions underway |
| **Won** | Deal closed, member acquired |
| **Lost** | Deal didn't close |

### Working with Cards

**Creating a Card:**
1. Click **+ Add Card** at the bottom of any column
2. Enter the company name
3. Click **Create**

**Moving Cards:**
- **Drag and drop** cards between columns
- Or open the card and change its status

**Opening Card Details:**
- Click on any card to open the detail modal
- Edit company info, add contacts, create proposals

### Card Features

Each card can contain:
- Company details (name, address, phone, website)
- Key contacts
- Notes and activity history
- Proposals
- Event/webinar scheduling
- Operations checklist
- Media production checklist

### Filtering & Search

Use the search bar at the top to find cards by company name. Filter options allow viewing by:
- All cards
- My cards (assigned to you)
- Specific relationship owners
      `
    },
    'companies': {
      title: 'Companies Management',
      content: `
## Companies (Nurturing List)

The Companies page provides a **list view** of all companies in your CRM.

### Viewing Companies

Access via **CRM → Companies** to see:
- Company name
- Industry
- Relationship status
- Relationship owner
- Last activity date

### Company Actions

From the list, you can:
- **Click** a company to open its card details
- **Search** using the search bar
- **Filter** by status or owner

### Company Information

Each company record contains:

| Field | Description |
|-------|-------------|
| Company Name | Legal business name |
| Industry | Business sector |
| Address | Full business address |
| City/Town | City location |
| Zip Code | Postal code |
| Country | Country of registration |
| Phone | Company phone number |
| Website | Company website URL |
| Registration | Company registration number |

### Key People

Add contacts associated with the company:
- Main Contact
- Billing Contact
- CMO, CRO, CEO
- Other contacts

Each contact can have:
- First name, Last name
- Email address
- Phone number
- Role/Title
      `
    },
    'contacts': {
      title: 'Contacts Management',
      content: `
## Contacts Management

The Contacts page shows all individual contacts across all companies.

### Accessing Contacts

Go to **CRM → Contacts** to see the full contacts list.

### Contact Information

Each contact displays:
- Full name
- Email address
- Phone number
- Company association
- Role within the company

### Adding Contacts

Contacts are typically added through:
1. **Card Details** - Add key people to a company card
2. **Proposal Recipients** - Add new contacts when sending proposals

### Contact Roles

Standard roles include:
- Main Contact
- Billing Contact
- CMO (Chief Marketing Officer)
- CRO (Chief Revenue Officer)
- CEO (Chief Executive Officer)
- Speaker (for events)
- Other

### Using Contacts

Contacts are used for:
- Sending proposals
- Email communications
- Meeting scheduling
- Event speaker management
      `
    },
    'proposals': {
      title: 'Creating Proposals',
      content: `
## Proposals

Create and manage professional membership proposals.

### Proposal Workflow

\`\`\`
Draft → Sent → Viewed → Signed (or Declined)
\`\`\`

### Creating a Proposal

1. Go to **CRM → Proposals**
2. Click **Create Proposal**
3. Select a company card
4. Choose a template (optional)
5. Customize sections and pricing
6. Add recipients
7. Send the proposal

### Proposal Sections

Default sections include:
- Executive Summary
- Membership Benefits
- Pricing & Terms
- Next Steps

Each section has a **rich text editor** for formatting content.

### Pricing Options

Add line items from your product catalog:
- Select products
- Set quantities
- Adjust prices if needed
- VAT is calculated automatically

You can create **multiple pricing options** (e.g., Monthly vs Annual).

### Sending Proposals

1. Select recipients from the card's contacts
2. Click **Send**
3. The message is pre-filled from your email template
4. Edit if needed, then confirm send

Each recipient gets a unique tracking link.

### Tracking & Signatures

Once sent, you can track:
- When the proposal was viewed
- E-signatures when signed
- Decline reasons if rejected

### After Signing

When a proposal is signed:
- A contract is automatically created
- Payment links become available
- The card status can be updated
      `
    },
    'contracts': {
      title: 'Contracts & Payments',
      content: `
## Contracts & Payments

Manage signed contracts and track payments.

### Accessing Contracts

Go to **CRM → Contracts & Payments** to see all contracts.

### Contract Information

Each contract shows:
- Company name
- Contract value (GBP)
- Start date
- Renewal date
- Payment status
- Total paid vs. remaining

### Payment Recording

**Recording a Manual Payment:**

1. Find the contract
2. Click **Record Payment**
3. Select payment method:
   - Stripe Invoice
   - Stripe Card
   - Stripe Bank Transfer
   - Revolut Card
   - Revolut Bank Transfer
   - Bank Transfer
   - Cheque
   - Cash
   - Other
4. Enter amount and reference
5. Click **Record**

Payments are automatically logged with timestamps and added as notes to the company card.

### Payment History

Expand any contract row to see:
- All payments made
- Payment dates
- Payment methods
- Reference numbers

### Renewal Tracking

The system tracks renewal dates and can:
- Show upcoming renewals
- Auto-update status to "Renewing"
- Generate renewal reminders
      `
    },
    'card-details': {
      title: 'Card Details & Notes',
      content: `
## Card Details Modal

The card detail modal is the central hub for all company information.

### Accessing Card Details

Click on any card in the Member Tracker or any company in the list views.

### Tabs Available

| Tab | Contents |
|-----|----------|
| **Details** | Company info, KYC, relationship status |
| **Contacts** | Key people and contacts |
| **Notes** | Activity notes and history |
| **Proposals** | All proposals for this company |
| **Webinar** | Event scheduling and speaker info |
| **Operations** | Project operations checklist |
| **Media** | Media production checklist |
| **History** | Full activity history |

### Adding Notes

1. Go to the **Notes** tab
2. Type your note in the text area
3. Click **Add Note**

Notes are timestamped and show the author.

### Operations Checklist

Track project milestones:
- Contract Sent
- Contract Signed
- Onboarding Call Scheduled
- Welcome Pack Sent
- And more...

Check items off to track progress. Completion dates are automatically recorded.

### Color Pinning

Pin important cards with colors:
- Click the pin icon
- Select a color (Red, Orange, Yellow, Green, Blue, Purple)
- Pinned cards appear at the top of columns
      `
    },
    'relationship-status': {
      title: 'Relationship Statuses',
      content: `
## Relationship Statuses

Track the lifecycle stage of each company.

### Status Definitions

| Status | Description |
|--------|-------------|
| **Suspect** | New lead, not yet qualified |
| **Prospect** | Qualified lead or proposal created |
| **Customer** | Contract signed, active member |
| **Paused** | Membership on hold |
| **Renewing** | Within 2 months of contract anniversary |
| **Churned** | Did not renew membership |

### Changing Status

**Admin users** can change relationship status:
1. Open card details
2. Go to the **Details** tab
3. Find "Relationship Status" dropdown
4. Select the new status
5. Save changes

### Status Automation

Some status changes happen automatically:
- **Renewing**: Triggered 2 months before renewal date
- **Churned**: Can trigger card retirement

### Relationship Owner

Assign a relationship owner to each card:
- Only admins can change owners
- Owner is responsible for the account
- Used for filtering and reporting
      `
    },

    // TASKS
    'tasks-overview': {
      title: 'Tasks Overview',
      content: `
## Task Management

Keep track of all your to-dos with the built-in task system.

### Task Views

Access tasks via the **Tasks** menu:

| View | Shows |
|------|-------|
| **My Tasks** | Tasks assigned to you |
| **Today's Tasks** | Tasks due today |
| **Overdue Tasks** | Past-due tasks |
| **All Tasks** | Every task in the system |

### Task Information

Each task contains:
- Title
- Description
- Due date
- Priority (High, Medium, Low)
- Assigned user
- Related card (optional)
- Status (To Do, In Progress, Done)

### Task Urgency Indicators

The sidebar shows urgency badges:
- **Red badge**: Overdue tasks exist
- **Orange bar**: High urgency score
- **Yellow bar**: Moderate urgency
- **Green**: All caught up!

The urgency score considers:
- Number of overdue tasks
- How many days overdue
- Tasks due today
      `
    },
    'creating-tasks': {
      title: 'Creating Tasks',
      content: `
## Creating Tasks

### From the Tasks Page

1. Go to **Tasks**
2. Click **+ New Task**
3. Fill in the details:
   - Title (required)
   - Description
   - Due date
   - Priority
   - Assignee
4. Click **Create**

### From a Card

1. Open any card detail modal
2. Tasks can be linked to cards for context

### Task Fields

| Field | Required | Description |
|-------|----------|-------------|
| Title | Yes | Brief task description |
| Description | No | Detailed information |
| Due Date | No | When the task should be completed |
| Priority | No | High, Medium, or Low |
| Assignee | No | Who should complete the task |
| Card | No | Link to a company card |

### Best Practices

> **Tip:** Use clear, action-oriented titles like "Follow up with Acme Corp on proposal"

> **Tip:** Set realistic due dates to maintain accurate urgency scores

> **Tip:** Link tasks to cards for better context and tracking
      `
    },
    'task-views': {
      title: 'Task Views & Filters',
      content: `
## Task Views & Filters

### Available Views

**My Tasks**
Shows only tasks assigned to you, grouped by status.

**Today's Tasks**
Tasks due today, regardless of assignee. Great for daily planning.

**Overdue Tasks**
All tasks past their due date. Shows how many days overdue.

**All Tasks**
Complete list of all tasks in the system.

### Filtering Tasks

Use the filter options to narrow results:
- **Status**: To Do, In Progress, Done
- **Priority**: High, Medium, Low
- **Assignee**: Filter by team member
- **Date Range**: Custom date filtering

### Sorting

Click column headers to sort by:
- Due date
- Priority
- Title
- Status

### Search

Use the search bar to find tasks by:
- Title
- Description
- Related card name
      `
    },
    'task-urgency': {
      title: 'Task Urgency System',
      content: `
## Task Urgency System

The CRM uses a weighted urgency scoring system.

### How It Works

The system calculates an urgency score based on:

\`\`\`
Urgency Score = (Overdue Days × Weight) + (Today Tasks × Weight) + (Upcoming × Weight)
\`\`\`

### Urgency Levels

| Score | Level | Indicator |
|-------|-------|-----------|
| 0 | None | Green - All caught up! |
| 1-14 | Low | Yellow bar |
| 15-29 | Medium | Orange bar |
| 30-49 | High | Red bar |
| 50+ | Critical | Dark red badge |

### Visual Indicators

The **Tasks** menu item shows:
- Colored left border indicating urgency level
- Badge with overdue task count
- Expands to show sub-views with individual counts

### Reducing Urgency

To lower your urgency score:
1. Complete overdue tasks (highest impact)
2. Complete today's tasks
3. Reschedule tasks that can't be completed on time

### Automatic Updates

Urgency scores update automatically:
- Every 60 seconds while using the system
- When tasks are created, updated, or completed
      `
    },

    // ANALYTICS
    'dashboard': {
      title: 'Dashboard Overview',
      content: `
## Dashboard

The Dashboard provides a high-level overview of your CRM performance.

### Key Metrics

The dashboard displays:
- **Total Companies**: Count of all companies in the system
- **Active Customers**: Companies with "Customer" status
- **Total Contract Value**: Sum of all contract values
- **Tasks Due**: Upcoming and overdue tasks

### Pipeline Overview

Visual representation of:
- Cards by column (stage)
- Conversion rates between stages
- Pipeline velocity

### Recent Activity

Shows recent:
- Card movements
- New proposals
- Payment recordings
- Task completions

### Quick Actions

From the dashboard:
- Jump to overdue tasks
- View new leads
- Access pending proposals
      `
    },
    'intelligence': {
      title: 'Intelligence Dashboard',
      content: `
## Intelligence Dashboard

Advanced analytics and reporting.

### Reports Available

| Report | Description |
|--------|-------------|
| **Pipeline Analysis** | Stage-by-stage breakdown |
| **Revenue Forecast** | Projected income |
| **Activity Metrics** | Team activity tracking |
| **Conversion Rates** | Lead to customer conversion |

### Time Filters

Filter data by:
- This Week
- This Month
- This Quarter
- This Year
- Custom date range

### Exporting Data

Reports can be exported for external analysis.

### Data Refresh

Intelligence data updates in real-time as you work in the CRM.
      `
    },
    'goals': {
      title: 'Goals Tracking',
      content: `
## Goals

Set and track business goals.

### Goal Types

- Revenue targets
- Customer acquisition
- Activity goals
- Custom metrics

### Setting Goals

*This feature is coming soon*

### Goal Progress

Track progress against:
- Monthly targets
- Quarterly objectives
- Annual goals

### Team Goals

Set goals for:
- Individual users
- Teams
- Company-wide
      `
    },

    // OPERATIONS
    'events-calendar': {
      title: 'Events Calendar',
      content: `
## Events Calendar

Manage webinars, meetings, and events.

### Calendar Views

- **Month View**: Full month at a glance
- **Week View**: Detailed weekly view
- **Day View**: Single day with times

### Event Types

| Type | Purpose |
|------|---------|
| **Webinar** | Online presentations |
| **Meeting** | Client meetings |
| **Event** | In-person events |
| **Other** | Custom event types |

### Creating Events

From a Card:
1. Open card details
2. Go to **Webinar** tab
3. Set the event date
4. Add speaker information
5. Save

### Event Fields

- Event name
- Date and time
- Speakers
- Description
- Related company
- Asset deadlines

### Speaker Management

For each speaker:
- Name and title
- Email address
- Photo upload
- Upload portal link
      `
    },
    'media-production': {
      title: 'Media Production Board',
      content: `
## Media Production Board

Track media production workflows for member events.

### Board Structure

The Media Production board follows a workflow:

\`\`\`
Pre-Event Setup → Content Creation → Event Marketing → Event & Recording → Video Production → Distribution → Advertising
\`\`\`

### Accessing the Board

Go to **Operations → Media Production**

### Card Integration

Media production items are linked to member cards. When a card has an event scheduled, it can appear on the media production board.

### Checklist Items

Each section contains checklist items:

**Pre-Event Setup:**
- Logos on Site
- Event Date
- Speaker Confirmed
- Press Release Written
- PR Release Date

**Content Creation:**
- Article Header Image
- Speaker Bio on Site

**Event Marketing:**
- LinkedIn Event Created
- Newsletter inclusion
- Zapier connection
- Platform listings

**Distribution:**
- YouTube upload
- Article published
- Social media scheduling

### Budget Tracking

Advertising items require budget entry:
- Social Ads (LinkedIn, YouTube, etc.)
- Pre-event advertising

The system calculates:
- Total ad budget
- Estimated clicks (based on $0.15/click)
      `
    },
    'operations-checklist': {
      title: 'Operations Checklist',
      content: `
## Operations Checklist

Track project milestones within each card.

### Accessing the Checklist

1. Open a card detail modal
2. Go to the **Operations** tab

### Checklist Items

Standard items include:
- Contract Sent
- Contract Signed
- Payment Received
- Onboarding Call Scheduled
- Onboarding Call Completed
- Welcome Pack Sent
- Platform Access Granted
- First Event Scheduled
- Relationship Review Done

### Tracking Progress

- Check off completed items
- See who completed each item
- View completion dates
- Track overall progress percentage

### Progress Bar

The Operations tab shows a progress bar indicating overall completion percentage.

### Best Practices

> **Tip:** Check items off as soon as they're completed for accurate tracking

> **Tip:** Use the completion dates to identify bottlenecks in your process
      `
    },

    // ADMIN
    'admin-overview': {
      title: 'Admin Overview',
      content: `
## Administration

Admin features are available to users with the Admin role.

### Admin Menu

Access admin features via the **Admin** section in the sidebar:

| Feature | Purpose |
|---------|---------|
| Users | Manage team members |
| Products | Product catalog |
| Payment Settings | Stripe/Revolut configuration |
| Data Import/Export | Bulk data operations |
| Help Videos | Tutorial video URLs |
| Product Backlog | Feature requests |
| Manage Requests | Support tickets |
| Workflow Editors | Customize board columns |
| Email Templates | Email content |
| System Emails | Transactional emails |
| Landing Page | Website CMS |
| Brevo Integration | Email marketing |
| Gmail Settings | Email sync |

### Admin Best Practices

> **Important:** Admin changes affect all users. Test in sandbox mode when available.

> **Tip:** Document any configuration changes you make.

> **Tip:** Regularly review user access and permissions.
      `
    },
    'user-management': {
      title: 'User Management',
      content: `
## User Management

Manage team access and roles.

### Accessing User Management

Go to **Admin → Users**

### User Roles

| Role | Access |
|------|--------|
| **Admin** | Full system access, can manage users |
| **User** | Standard CRM access |

### Adding Users

Users are added automatically when they:
1. Log in with Google
2. Are part of your organization domain

### User Information

Each user record shows:
- Name
- Email
- Role
- Job Title
- Last login

### Changing Roles

To change a user's role (Admin only):
1. Find the user in the list
2. Click on their row
3. Change the role dropdown
4. Save changes

### Removing Access

To remove a user's access:
1. Find the user
2. Click **Deactivate**
3. Confirm the action

Deactivated users cannot log in but their data is preserved.
      `
    },
    'products': {
      title: 'Products Catalog',
      content: `
## Products Catalog

Manage your product and service offerings.

### Accessing Products

Go to **Admin → Products**

### Product Information

Each product contains:
- Name
- Description
- Pricing variants
- Billing frequency
- VAT settings

### Pricing Variants

Products can have multiple pricing options:
- Monthly subscription
- Annual subscription
- One-time payment
- Consultancy (hourly/project)

### Creating Products

1. Click **+ New Product**
2. Enter product name
3. Add description
4. Configure pricing variants
5. Set VAT if applicable
6. Save

### Using Products

Products appear in:
- Proposal line items
- Quote generation
- Contract creation

### Best Practices

> **Tip:** Use clear, descriptive product names

> **Tip:** Set up variants for common pricing scenarios

> **Tip:** Keep VAT settings consistent
      `
    },
    'payment-settings': {
      title: 'Payment Settings',
      content: `
## Payment Settings

Configure payment providers.

### Accessing Payment Settings

Go to **Admin → Payment Settings**

### Supported Providers

| Provider | Types |
|----------|-------|
| **Stripe** | Cards, Bank, Invoices |
| **Revolut** | Cards, Bank transfers |

### Stripe Configuration

Enter your Stripe credentials:
- Publishable Key
- Secret Key
- Webhook Secret

These are obtained from your Stripe dashboard.

### Revolut Configuration

Enter your Revolut credentials:
- API Key
- Webhook configuration

### Payment Methods

Configure which payment methods are available:
- Credit/Debit Cards
- Bank Transfers
- Invoicing
- Manual recording (Cheque, Cash)

### Testing Payments

Use Stripe test mode for development:
- Test card: 4242 4242 4242 4242
- Any future expiry date
- Any 3-digit CVC

### Webhooks

Webhooks automatically update payment status:
- Payment completed
- Payment failed
- Refunds processed
      `
    },
    'data-import': {
      title: 'Data Import/Export',
      content: `
## Data Import/Export

Bulk data operations for migration and backup.

### Accessing

Go to **Admin → Data Import/Export**

### Importing Data

**CSV Import:**

1. Download the template CSV
2. Fill in your data following the format
3. Upload the CSV file
4. Preview the import
5. Review any errors
6. Confirm to execute

**Import Fields:**
- Company Name (required)
- Contact details
- Address information
- Contract data
- Payment records

### Exporting Data

**Export All:**
Download a complete backup of:
- All companies
- All contacts
- All contracts
- All payments

The export is a CSV file ready for:
- Backup storage
- External analysis
- System migration

### Best Practices

> **Important:** Always backup before importing

> **Tip:** Review the preview carefully before executing imports

> **Tip:** Export regularly for data backup
      `
    },
    'workflow-editor': {
      title: 'Workflow Editor',
      content: `
## Workflow Editor

Customize board columns and workflow stages.

### Accessing

- **Member Tracker Workflow:** Admin → Edit Members Workflow
- **Media Production Workflow:** Admin → Edit Media Workflow

### What Can Be Customized

| Element | Customizable |
|---------|--------------|
| Column names | ✓ |
| Column order | ✓ |
| Number of columns | ✓ |
| Column colors | ✓ |

### Editing Workflow

1. Open the workflow editor
2. Drag columns to reorder
3. Click column names to rename
4. Add new columns with + button
5. Remove columns (cards will move to adjacent column)
6. Save changes

### Best Practices

> **Important:** Changing workflows affects all users immediately

> **Tip:** Plan your workflow stages before making changes

> **Tip:** Fewer columns with clear definitions work better than many ambiguous stages
      `
    },
    'email-templates': {
      title: 'Email Templates',
      content: `
## Email Templates

Manage proposal email templates.

### Accessing

Go to **Admin → Email Templates**

### Template Uses

Templates are used for:
- Proposal cover letters
- Follow-up emails
- Custom communications

### Template Variables

Use variables in your templates:

| Variable | Replaced With |
|----------|---------------|
| \`{{recipient_first_name}}\` | Contact's first name |
| \`{{recipient_name}}\` | Full name |
| \`{{company_name}}\` | Company name |
| \`{{proposal_link}}\` | Link to proposal |
| \`{{sender_name}}\` | Your name |

### Creating Templates

1. Click **+ New Template**
2. Enter template name
3. Write subject line
4. Compose body using the editor
5. Insert variables as needed
6. Save

### Using Templates

When sending proposals:
1. Template is automatically loaded
2. Variables are replaced with real data
3. You can edit before sending
      `
    },
    'system-emails': {
      title: 'System Emails',
      content: `
## System Emails

Configure automated transactional emails.

### Accessing

Go to **Admin → System Emails**

### Email Categories

| Category | Emails |
|----------|--------|
| **Proposals** | New proposal, payment request, thank you |
| **Events** | Reminders, confirmations, follow-ups |
| **CRM** | Renewal reminders, new member welcome |
| **Reporting** | Goal updates, task summaries |
| **Support** | Ticket received, resolved |

### Editing System Emails

1. Select a category
2. Click on an email template
3. Edit subject and body
4. Use the visual editor or HTML mode
5. Preview your changes
6. Save

### Visual Editor Features

The system email editor includes:
- Rich text formatting (bold, italic, lists)
- Link insertion
- Variable insertion buttons
- HTML toggle for advanced editing
- Live preview

### Variables

Each email type has specific variables:
- View available variables in the editor sidebar
- Click to insert into your email

### Testing

Use preview mode to see how emails will appear with sample data.
      `
    },
    'integrations': {
      title: 'Integrations (Brevo, Gmail)',
      content: `
## Integrations

Connect external services.

### Brevo (Email Marketing)

**Purpose:** Send transactional emails

**Setup:**
1. Go to **Admin → Brevo Integration**
2. Enter your Brevo API key
3. Configure sender email
4. Test the connection

**Features:**
- Transactional email sending
- Email tracking
- Delivery reports

### Gmail Integration

**Purpose:** Sync email communications

**Setup:**
1. Go to **Admin → Gmail Settings**
2. Connect your Google Workspace account
3. Configure sync settings

**Features:**
- Automatic email sync
- Email history on cards
- Manual sync trigger

**Manual Sync:**
- Select time window (24 hours, 7 days, 30 days)
- Click "Sync Now"
- Emails are matched to contacts and attached to cards

### Best Practices

> **Important:** Only connect official company accounts

> **Tip:** Regular syncs ensure email history is up to date

> **Tip:** Check integration status regularly
      `
    },
    'help-videos-admin': {
      title: 'Managing Help Videos',
      content: `
## Managing Help Videos

Configure tutorial videos for each section.

### Accessing

Go to **Admin → Help Videos**

### Video Sections

Configure videos for:
- Dashboard
- Member Tracker
- Companies
- Contacts
- Proposals
- Contracts
- Intelligence
- Goals
- Events
- Media Production
- Tasks
- Documentation
- Admin sections

### Adding Videos

1. Find the section in the list
2. Enter the YouTube URL
3. Click Save

**Supported URL formats:**
- \`https://www.youtube.com/watch?v=VIDEO_ID\`
- \`https://youtu.be/VIDEO_ID\`
- \`https://www.youtube.com/embed/VIDEO_ID\`

### Removing Videos

Clear the URL field and save to remove a video.

### Video Display

Videos appear:
- In a popup overlay when users click the play button
- Autoplay is enabled
- Users can close the overlay anytime
      `
    },

    // SETTINGS
    'profile': {
      title: 'Your Profile',
      content: `
## Your Profile

Manage your personal settings.

### Accessing Settings

Click the **Settings** (gear icon) in the sidebar.

### Profile Information

View and edit:
- **Name:** Your display name
- **Email:** Login email (read-only)
- **Job Title:** Your role/position
- **Role:** User or Admin (read-only)

### Changing Job Title

1. Go to Settings
2. Find "Job Title" field
3. Enter your title
4. Click Save

Your job title appears:
- In system emails ({{sender_title}} variable)
- In user management lists
      `
    },
    'theme': {
      title: 'Theme Settings',
      content: `
## Theme Settings

Customize your visual experience.

### Dark Mode / Light Mode

Toggle between themes using the sun/moon icon in the header.

**Dark Mode:**
- Dark background
- Green accent colors
- Easier on eyes in low light

**Light Mode:**
- White background
- Green accent colors
- Better for bright environments

### Theme Persistence

Your theme preference is saved and remembered across sessions.
      `
    },

    // TROUBLESHOOTING
    'common-issues': {
      title: 'Common Issues',
      content: `
## Common Issues

Solutions to frequently encountered problems.

### Login Issues

**Problem:** Can't log in with Google

**Solutions:**
1. Ensure you're using the correct Google account
2. Clear browser cache and cookies
3. Try incognito/private browsing mode
4. Check if your organization allows the app

### Data Not Saving

**Problem:** Changes don't appear to save

**Solutions:**
1. Check your internet connection
2. Look for error messages
3. Refresh the page
4. Try again in a new browser tab

### Slow Performance

**Problem:** Pages load slowly

**Solutions:**
1. Check your internet speed
2. Clear browser cache
3. Close unused browser tabs
4. Try a different browser

### Payment Issues

**Problem:** Payment not processing

**Solutions:**
1. Verify payment provider settings (Admin)
2. Check API keys are correct
3. Ensure webhooks are configured
4. Review payment provider dashboard for errors

### Missing Data

**Problem:** Data seems to be missing

**Solutions:**
1. Check filters - they may be hiding data
2. Search for the specific item
3. Verify you have the correct permissions
4. Contact admin if data is truly missing
      `
    },
    'support-requests': {
      title: 'Submitting Support Requests',
      content: `
## Submitting Support Requests

Report bugs and request features.

### Accessing

Go to **Support → Bugs & Requests**

### Request Types

| Type | Use For |
|------|---------|
| **Bug Report** | Something isn't working correctly |
| **Feature Request** | New functionality you'd like |
| **Question** | General inquiries |
| **Other** | Anything else |

### Submitting a Request

1. Click **New Request**
2. Select the type
3. Enter a clear title
4. Describe the issue in detail
5. Add screenshots if helpful
6. Submit

### Good Bug Reports Include

- What you were trying to do
- What happened instead
- Steps to reproduce the issue
- Browser and device information
- Any error messages you saw

### Request Status

Requests go through stages:
- **New** - Just submitted
- **In Review** - Being looked at
- **In Progress** - Being worked on
- **Resolved** - Fixed/Completed
- **Closed** - No action needed

### Getting Updates

You'll be notified when your request status changes.
      `
    }
  };

  // Filter articles based on search
  const filteredSections = sections.map(section => ({
    ...section,
    articles: section.articles.filter(article =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (articleContent[article.id]?.content || '').toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(section => section.articles.length > 0);

  const currentArticle = articleContent[activeArticle];

  // Simple markdown-to-JSX renderer
  const renderMarkdown = (content) => {
    if (!content) return null;
    
    const lines = content.trim().split('\n');
    const elements = [];
    let inTable = false;
    let tableRows = [];
    let inCodeBlock = false;
    let codeContent = [];
    let listItems = [];
    let inList = false;

    const processLine = (line, index) => {
      // Code blocks
      if (line.trim().startsWith('```')) {
        if (inCodeBlock) {
          elements.push(
            <pre key={`code-${index}`} className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-100 border-gray-200'} border rounded p-4 overflow-x-auto my-4 text-sm`}>
              <code className={isDark ? 'text-gray-300' : 'text-gray-700'}>{codeContent.join('\n')}</code>
            </pre>
          );
          codeContent = [];
          inCodeBlock = false;
        } else {
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeContent.push(line);
        return;
      }

      // Tables
      if (line.trim().startsWith('|')) {
        if (!inTable) {
          inTable = true;
          tableRows = [];
        }
        if (!line.includes('---')) {
          tableRows.push(line.split('|').filter(cell => cell.trim()).map(cell => cell.trim()));
        }
        return;
      } else if (inTable) {
        // End of table
        const header = tableRows[0];
        const body = tableRows.slice(1);
        elements.push(
          <div key={`table-${index}`} className="overflow-x-auto my-4">
            <table className={`w-full ${isDark ? 'border-white/10' : 'border-gray-200'} border rounded`}>
              <thead className={isDark ? 'bg-[#0A0A0A]' : 'bg-gray-100'}>
                <tr>
                  {header.map((cell, i) => (
                    <th key={i} className={`px-4 py-2 text-left text-sm font-semibold ${isDark ? 'text-white border-white/10' : 'text-gray-900 border-gray-200'} border-b`}>
                      {cell.replace(/\*\*/g, '')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {body.map((row, rowIndex) => (
                  <tr key={rowIndex} className={isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex} className={`px-4 py-2 text-sm ${isDark ? 'text-gray-300 border-white/10' : 'text-gray-700 border-gray-200'} border-b`}>
                        {cell.replace(/\*\*/g, '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        tableRows = [];
        inTable = false;
      }

      // Headers
      if (line.startsWith('## ')) {
        if (inList) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 my-2 space-y-1">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(
          <h2 key={index} className={`text-xl font-bold mt-6 mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {line.replace('## ', '')}
          </h2>
        );
        return;
      }

      if (line.startsWith('### ')) {
        if (inList) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 my-2 space-y-1">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(
          <h3 key={index} className={`text-lg font-semibold mt-4 mb-2 ${isDark ? 'text-[#A2FF00]' : 'text-green-700'}`}>
            {line.replace('### ', '')}
          </h3>
        );
        return;
      }

      // Blockquotes (tips, important notes)
      if (line.startsWith('> ')) {
        if (inList) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 my-2 space-y-1">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        const content = line.replace('> ', '');
        const isImportant = content.toLowerCase().includes('important');
        const isTip = content.toLowerCase().includes('tip');
        elements.push(
          <div key={index} className={`my-3 p-3 rounded border-l-4 ${
            isImportant 
              ? (isDark ? 'bg-red-500/10 border-red-500 text-red-400' : 'bg-red-50 border-red-500 text-red-700')
              : isTip
                ? (isDark ? 'bg-[#A2FF00]/10 border-[#A2FF00] text-[#A2FF00]' : 'bg-green-50 border-green-500 text-green-700')
                : (isDark ? 'bg-blue-500/10 border-blue-500 text-blue-400' : 'bg-blue-50 border-blue-500 text-blue-700')
          }`}>
            <div className="flex items-start gap-2">
              {isImportant && <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
              {isTip && <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />}
              {!isImportant && !isTip && <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />}
              <span className="text-sm">{content.replace(/\*\*(.*?)\*\*/g, '$1')}</span>
            </div>
          </div>
        );
        return;
      }

      // List items
      if (line.trim().startsWith('- ')) {
        inList = true;
        const content = line.trim().replace('- ', '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        listItems.push(
          <li key={`li-${index}`} className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`} dangerouslySetInnerHTML={{ __html: sanitizeHTML(content) }} />
        );
        return;
      } else if (inList && line.trim() === '') {
        elements.push(<ul key={`list-${index}`} className="list-disc pl-6 my-2 space-y-1">{listItems}</ul>);
        listItems = [];
        inList = false;
      }

      // Numbered list
      if (/^\d+\.\s/.test(line.trim())) {
        if (inList) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 my-2 space-y-1">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        const content = line.trim().replace(/^\d+\.\s/, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        elements.push(
          <div key={index} className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'} ml-4 my-1`}>
            <span className={isDark ? 'text-[#A2FF00]' : 'text-green-600'}>{line.trim().match(/^\d+/)[0]}.</span>{' '}
            <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(content) }} />
          </div>
        );
        return;
      }

      // Regular paragraph
      if (line.trim() && !inTable) {
        if (inList) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 my-2 space-y-1">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        let content = line
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/`([^`]+)`/g, `<code class="${isDark ? 'bg-[#0A0A0A] text-[#A2FF00]' : 'bg-gray-100 text-green-700'} px-1 rounded text-sm">$1</code>`);
        elements.push(
          <p key={index} className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'} my-2 leading-relaxed`} dangerouslySetInnerHTML={{ __html: sanitizeHTML(content) }} />
        );
      }
    };

    lines.forEach(processLine);

    // Handle any remaining list
    if (inList && listItems.length > 0) {
      elements.push(<ul key="list-end" className="list-disc pl-6 my-2 space-y-1">{listItems}</ul>);
    }

    return elements;
  };

  return (
    <AppLayout>
      <div className="flex h-[calc(100vh-64px)]">
        {/* Sidebar - Table of Contents */}
        <div className={`w-72 flex-shrink-0 ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border-r overflow-y-auto`}>
          <div className="p-4">
            <h1 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Documentation
            </h1>
            
            {/* Search */}
            <div className="relative mb-4">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Search docs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded ${isDark ? 'bg-[#111] border-white/10 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'} border text-sm focus:outline-none focus:ring-2 focus:ring-[#A2FF00]/50`}
              />
            </div>

            {/* Navigation */}
            <nav className="space-y-1">
              {filteredSections.map((section) => {
                const Icon = section.icon;
                const isExpanded = expandedSections[section.id];
                const hasActiveArticle = section.articles.some(a => a.id === activeArticle);

                return (
                  <div key={section.id}>
                    <button
                      onClick={() => toggleSection(section.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors ${
                        hasActiveArticle
                          ? (isDark ? 'bg-[#A2FF00]/10 text-[#A2FF00]' : 'bg-green-100 text-green-700')
                          : (isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100')
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="flex-1 text-left">{section.title}</span>
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>

                    {isExpanded && (
                      <div className="ml-6 mt-1 space-y-1">
                        {section.articles.map((article) => (
                          <button
                            key={article.id}
                            onClick={() => setActiveArticle(article.id)}
                            className={`w-full text-left px-3 py-1.5 rounded text-sm transition-colors ${
                              activeArticle === article.id
                                ? (isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00]' : 'bg-green-200 text-green-800')
                                : (isDark ? 'text-gray-500 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100')
                            }`}
                          >
                            {article.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-8">
            {currentArticle ? (
              <>
                <h1 className={`text-3xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {currentArticle.title}
                </h1>
                <div className={`prose ${isDark ? 'prose-invert' : ''} max-w-none`}>
                  {renderMarkdown(currentArticle.content)}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <BookOpen className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                <h2 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Select an article
                </h2>
                <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                  Choose a topic from the sidebar to get started
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default Documentation;
